import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { HiArrowUpRight } from "react-icons/hi2";
import api from "../../lib/api";
import AIPanel from "./AIPanel";
import TagInput from "./TagInput";
import ImageUploader from "./ImageUploader";
import { CATEGORIES } from "./categories";

const EMPTY = {
  id: null,
  slug: "",
  title: "",
  excerpt: "",
  markdown: "",
  tags: [],
  category: "",
  coverImage: "",
  status: "draft",
  seo: { title: "", description: "", keywords: [] },
};

export default function Editor() {
  const { id } = useParams();
  const isNew = !id;
  const nav = useNavigate();

  const [post, setPost] = useState(EMPTY);
  const [loading, setLoading] = useState(!isNew);
  const [savingState, setSavingState] = useState(null); // null | "saving" | "saved" | "error"
  const [error, setError] = useState(null);
  const [view, setView] = useState("write"); // write | preview

  useEffect(() => {
    if (isNew) return;
    let live = true;
    setLoading(true);
    api
      .get(`/api/admin/posts/${id}`)
      .then(({ data }) => {
        if (!live) return;
        setPost({
          ...EMPTY,
          ...data.post,
          seo: { ...EMPTY.seo, ...(data.post.seo || {}) },
        });
      })
      .catch((e) => { if (live) setError(e?.response?.data?.error || e.message); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [id, isNew]);

  const setField = (k, v) => setPost((p) => ({ ...p, [k]: v }));
  const setSeo = (k, v) => setPost((p) => ({ ...p, seo: { ...p.seo, [k]: v } }));

  const persist = async (overrides = {}) => {
    setSavingState("saving");
    setError(null);
    const body = {
      title: post.title,
      slug: post.slug,
      excerpt: post.excerpt,
      markdown: post.markdown,
      tags: post.tags,
      category: post.category,
      coverImage: post.coverImage,
      seo: post.seo,
      ...overrides,
    };
    try {
      let saved;
      if (isNew && !post.id) {
        const { data } = await api.post("/api/admin/posts", body);
        saved = data.post;
        setPost({ ...EMPTY, ...saved, seo: { ...EMPTY.seo, ...(saved.seo || {}) } });
        nav(`/admin/edit/${saved.id}`, { replace: true });
      } else {
        const targetId = post.id || id;
        const { data } = await api.patch(`/api/admin/posts/${targetId}`, body);
        saved = data.post;
        setPost({ ...EMPTY, ...saved, seo: { ...EMPTY.seo, ...(saved.seo || {}) } });
      }
      setSavingState("saved");
      setTimeout(() => setSavingState(null), 1500);
      return saved;
    } catch (e) {
      setSavingState("error");
      setError(e?.response?.data?.error || e.message);
      throw e;
    }
  };

  const onSaveDraft = async () => {
    try { await persist({ status: "draft" }); } catch (e) {}
  };

  const onPublish = async () => {
    if (!post.title || post.markdown.length < 200) {
      setError("Add a title and at least a paragraph or two before publishing.");
      return;
    }
    try { await persist({ status: "published" }); } catch (e) {}
  };

  const onUnpublish = async () => {
    try { await persist({ status: "draft" }); } catch (e) {}
  };

  const onApplyAI = (gen) => {
    setPost((p) => ({
      ...p,
      title: gen.title || p.title,
      slug: gen.slug || p.slug,
      excerpt: gen.excerpt || p.excerpt,
      markdown: gen.markdown || p.markdown,
      tags: Array.isArray(gen.tags) && gen.tags.length ? gen.tags : p.tags,
      category: gen.category || p.category,
      seo: {
        title: gen.seo?.title || p.seo.title,
        description: gen.seo?.description || p.seo.description,
        keywords: gen.seo?.keywords || p.seo.keywords,
      },
    }));
    setView("write");
  };

  if (loading) return <p className="text-muted">Loading post…</p>;

  return (
    <div>
      <div className="admin-head">
        <div>
          <span className="eyebrow">{isNew ? "New post" : "Edit post"}</span>
          <h1 style={{ marginTop: 8 }}>{post.title || "Untitled draft"}</h1>
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          {savingState === "saving" && <span className="text-muted" style={{ fontSize: 13 }}>Saving…</span>}
          {savingState === "saved" && <span className="success-msg" style={{ marginTop: 0 }}>Saved</span>}
          {post.status && (
            <span className={`status-pill ${post.status}`}>{post.status}</span>
          )}
        </div>
      </div>

      <div className="toolbar">
        <button
          type="button"
          className="chip"
          style={{
            cursor: "pointer",
            background: view === "write" ? "var(--accent)" : "transparent",
            color: view === "write" ? "#0a0a0a" : "var(--text-soft)",
            borderColor: view === "write" ? "var(--accent)" : "var(--border-strong)",
          }}
          onClick={() => setView("write")}
        >
          Write
        </button>
        <button
          type="button"
          className="chip"
          style={{
            cursor: "pointer",
            background: view === "preview" ? "var(--accent)" : "transparent",
            color: view === "preview" ? "#0a0a0a" : "var(--text-soft)",
            borderColor: view === "preview" ? "var(--accent)" : "var(--border-strong)",
          }}
          onClick={() => setView("preview")}
        >
          Preview
        </button>
        <span style={{ flex: 1 }} />
        <button type="button" className="btn btn--ghost" onClick={onSaveDraft}>
          Save draft
        </button>
        {post.status === "published" ? (
          <button type="button" className="btn btn--ghost" onClick={onUnpublish}>
            Unpublish
          </button>
        ) : (
          <button type="button" className="btn" onClick={onPublish}>
            Publish
            <span className="arrow"><HiArrowUpRight /></span>
          </button>
        )}
      </div>

      {error && <p style={{ color: "var(--danger)", marginBottom: 16 }}>{error}</p>}

      <div className="editor-grid">
        <div className="admin-card">
          <div className="field">
            <label htmlFor="title">Title</label>
            <input
              id="title"
              className="input"
              value={post.title}
              onChange={(e) => setField("title", e.target.value)}
              placeholder="Post title"
            />
          </div>

          <div className="field">
            <label htmlFor="slug">Slug</label>
            <input
              id="slug"
              className="input"
              value={post.slug}
              onChange={(e) => setField("slug", e.target.value)}
              placeholder="auto-generated-from-title"
            />
          </div>

          <ImageUploader
            label="Cover image"
            value={post.coverImage}
            onChange={(u) => setField("coverImage", u)}
            aiEnabled
            aiContext={{ title: post.title, excerpt: post.excerpt, tags: post.tags }}
            hint="Upload a JPG/PNG/WebP/SVG, paste a URL, or generate one with AI."
          />

          <div className="field">
            <label htmlFor="excerpt">Excerpt</label>
            <textarea
              id="excerpt"
              className="textarea"
              rows={2}
              style={{ minHeight: 60, fontFamily: "var(--font-body)" }}
              value={post.excerpt}
              onChange={(e) => setField("excerpt", e.target.value)}
              placeholder="One- or two-sentence summary used on the blog index and meta description fallback."
            />
          </div>

          <div className="field">
            <label htmlFor="category">Category</label>
            <select
              id="category"
              className="select"
              value={post.category || ""}
              onChange={(e) => setField("category", e.target.value)}
            >
              <option value="">— Pick a category —</option>
              {CATEGORIES.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>

          <div className="field">
            <label>Tags</label>
            <TagInput value={post.tags} onChange={(v) => setField("tags", v)} />
          </div>

          {view === "write" ? (
            <div className="field">
              <label htmlFor="md">Markdown</label>
              <textarea
                id="md"
                className="textarea textarea--lg"
                value={post.markdown}
                onChange={(e) => setField("markdown", e.target.value)}
                placeholder="# Heading

Write in Markdown. GFM tables, fenced code blocks, lists — all good."
              />
            </div>
          ) : (
            <div>
              <label style={{ fontFamily: "var(--font-display)", fontSize: 12, letterSpacing: "0.16em", textTransform: "uppercase", color: "var(--muted)" }}>
                Live preview
              </label>
              <div className="prose" style={{ marginTop: 12, padding: 24, border: "1px solid var(--border)", borderRadius: 16, background: "var(--bg)" }}>
                <h1 style={{ fontFamily: "var(--font-display)", fontSize: 36, marginBottom: 16, letterSpacing: "-0.02em", color: "var(--text)" }}>
                  {post.title || "Untitled"}
                </h1>
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {post.markdown || "_Nothing to preview yet._"}
                </ReactMarkdown>
              </div>
            </div>
          )}

          <details style={{ marginTop: 24, borderTop: "1px solid var(--border)", paddingTop: 24 }}>
            <summary style={{ cursor: "pointer", fontFamily: "var(--font-display)", fontSize: 13, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--muted)", marginBottom: 16 }}>
              SEO metadata
            </summary>
            <div className="field">
              <label htmlFor="seoT">SEO title</label>
              <input id="seoT" className="input" value={post.seo.title} onChange={(e) => setSeo("title", e.target.value)} />
              <small style={{ color: "var(--muted)", fontSize: 12, marginTop: 4 }}>
                {post.seo.title.length}/70
              </small>
            </div>
            <div className="field">
              <label htmlFor="seoD">Meta description</label>
              <textarea id="seoD" className="textarea" rows={2} style={{ minHeight: 60, fontFamily: "var(--font-body)" }}
                value={post.seo.description}
                onChange={(e) => setSeo("description", e.target.value)} />
              <small style={{ color: "var(--muted)", fontSize: 12, marginTop: 4 }}>
                {post.seo.description.length}/160
              </small>
            </div>
            <div className="field">
              <label>Keywords</label>
              <TagInput value={post.seo.keywords || []} onChange={(v) => setSeo("keywords", v)} />
            </div>
          </details>
        </div>

        <AIPanel onApply={onApplyAI} />
      </div>
    </div>
  );
}
